import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import IconContainer from "@/components/ui/icon-container";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { DetailedViewOptions } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { Edit } from "@dreamhub/designsystem/icons";
import {
  isRegenerable,
  isRevisable,
  isWithdrawable,
  withdrawsOnRevise
} from "../draftActions";
import {
  regenerateErrorMessageKey,
  reviseErrorMessageKey,
  withdrawErrorMessageKey
} from "../reviseErrorMessage";
import {
  ReviseProposalDialog,
  WithdrawAndRegenerateDialog,
  WithdrawProposalDialog
} from "../ProposalActionDialogs";
import {
  Proposal,
  ProposalDocument,
  ProposalEditorLocationState,
  ProposalStatus
} from "../types";
import { ProposalTitle } from "./ProposalTitle";
import { useProposalDownload } from "./useProposalDownload";
import {
  DocumentPane,
  DownloadButton,
  EditorHeader,
  PANE_ROW,
  ProposalActionsMenu
} from "./documentLayout";
import { regenerateOption, renameOption, withdrawOption } from "./menuOptions";
import { useProposalRename } from "./useProposalRename";
import { RecipientStatusList } from "../recipients/RecipientStatusList";
import {
  useProposals,
  useProposalTracking,
  useRefreshProposalDocument,
  useStoredProposalFile
} from "../hooks";
import { useTrackingPaused, withRecipientStates } from "../tracking";

export interface ReadOnlyProposalActions {
  onRename: (proposal: Proposal, name: string) => Promise<unknown>;
  // Returns the proposal to Draft as a new version, withdrawing it first when
  // it is still out for signature; resolves with the new draft.
  onRevise: (proposal: Proposal) => Promise<Proposal>;
  // Withdraws the signature request and leaves the proposal Voided; no new
  // draft follows, on a closed deal too.
  onWithdraw: (proposal: Proposal) => Promise<unknown>;
}

interface Props extends ReadOnlyProposalActions {
  dealId: string;
  // Carried to the new draft, so leaving it returns where this was opened from.
  returnTo?: string;
  dealName?: string;
  document: ProposalDocument;
  // Why Regenerate is unavailable; shown on the disabled option.
  regenerateBlockedReason?: string;
  // Holds Regenerate until the contract's Terms & Services are known.
  regenerateHeld?: boolean;
}

type Confirming = "revise" | "regenerate" | "withdraw";

// A proposal past Draft opens for reading: the document with its status, and
// the actions that move it on. What went out is never edited in place.
export const ReadOnlyProposalView: React.FC<Props> = ({
  dealId,
  returnTo,
  dealName,
  document,
  regenerateBlockedReason,
  regenerateHeld = false,
  onRename,
  onRevise,
  onWithdraw
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    name,
    rename,
    pending: renamePending
  } = useProposalRename(document.proposal, onRename);
  const [renaming, setRenaming] = useState(false);
  const [revising, setRevising] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const { downloading, download: downloadProposal } = useProposalDownload();
  const [confirming, setConfirming] = useState<Confirming>();

  const trackingPaused = useTrackingPaused();
  // The deal's list is re-read while a proposal can still move, so its status
  // and signing progress here follow the vendor without a reload.
  const { data: proposals } = useProposals(dealId);
  const listed = proposals?.find(
    (candidate) => candidate.id === document.proposal.id
  );
  // A cached list can trail the document right after a Send, so it counts
  // only when it is at least as new.
  const live =
    listed &&
    Date.parse(listed.updatedAt) >= Date.parse(document.proposal.updatedAt)
      ? listed
      : undefined;
  // Read for the newest row known, so a version stored since shows up here.
  const { data: tracking, isError: trackingFailed } = useProposalTracking(
    live ?? document.proposal
  );
  // Revised elsewhere: back in Draft, or already sent again as a newer
  // version. This one is no longer the proposal, so read it again rather than
  // mix the two.
  const superseded =
    live?.status === ProposalStatus.DRAFT ||
    (!!tracking?.versionId &&
      !!document.version &&
      tracking.versionId !== document.version.id);
  const refreshDocument = useRefreshProposalDocument(
    dealId,
    document.proposal.id
  );
  useEffect(() => {
    if (superseded) void refreshDocument();
  }, [superseded, refreshDocument]);
  const proposal = {
    ...document.proposal,
    ...(superseded ? undefined : live),
    name
  };
  const recipients = withRecipientStates(document.recipients, proposal);
  const busy = renamePending || revising || withdrawing;
  const signed = proposal.status === ProposalStatus.SIGNED;
  // A signed proposal downloads its signed file, so there is nothing to give
  // until it has been stored.
  const fileUnavailable =
    signed && !!tracking && !tracking.signedFileAttachmentId;
  // Held until the stored files are read, so Download never stands the composed
  // document in for the one that went out, and while a newer version is read.
  const filesPending = superseded || (!tracking && !trackingFailed);
  // What went out, or what came back signed; the composed document only for
  // one sent before its file was kept.
  const fileId = signed
    ? tracking?.signedFileAttachmentId
    : tracking?.sentFileAttachmentId;
  // On screen too: the composed document carries the DRAFT watermark, so it
  // stands in only for one sent before its file was kept.
  const storedFile = useStoredProposalFile(superseded ? undefined : fileId);
  // A file that went out but can't be read is reported, never replaced by the
  // composed document.
  const fileFailed = trackingFailed || storedFile.isError;
  const shownUrl = (() => {
    if (filesPending || fileFailed) return undefined;
    if (!fileId) return document.documentUrl;
    return storedFile.data?.documentUrl;
  })();

  // Both open the new draft; Regenerate then rebuilds it from the latest data.
  const returnToDraft = async (regenerate: boolean) => {
    setConfirming(undefined);
    setRevising(true);
    try {
      const draft = await onRevise(proposal);
      const state: ProposalEditorLocationState = {
        ...(regenerate ? { regenerate: true } : {}),
        ...(returnTo ? { returnTo } : {})
      };
      navigate(`/deals/${dealId}/proposals/${draft.id}`, {
        state: Object.keys(state).length > 0 ? state : undefined
      });
    } catch (error) {
      setRevising(false);
      // Nothing changed: the proposal stays as it was, and the rep can retry.
      toast.error(
        t(
          regenerate
            ? regenerateErrorMessageKey(error)
            : reviseErrorMessageKey(error)
        )
      );
    }
  };

  // The proposal stays open here, read again as Voided; a refusal leaves it as
  // it was, so the rep can try again.
  const withdraw = async () => {
    setConfirming(undefined);
    setWithdrawing(true);
    try {
      await onWithdraw(proposal);
      toast.success(t("contracts.proposals.withdrawn"));
    } catch (error) {
      toast.error(t(withdrawErrorMessageKey(error)));
    } finally {
      setWithdrawing(false);
    }
  };

  const download = async () => {
    if (trackingFailed) {
      toast.error(t("contracts.proposals.editor.downloadError"));
      return;
    }
    await downloadProposal({
      name,
      status: proposal.status,
      fileId,
      fallbackUrl: document.documentUrl
    });
  };

  const menuOptions: DetailedViewOptions = {
    rename: renameOption(() => setRenaming(true), busy),
    ...(isRegenerable(proposal)
      ? {
          regenerate: regenerateOption(
            () => setConfirming("regenerate"),
            busy || regenerateHeld,
            regenerateBlockedReason
          )
        }
      : {}),
    // Also on a closed deal, where there's no Revise.
    ...(isWithdrawable(proposal)
      ? { withdraw: withdrawOption(() => setConfirming("withdraw"), busy) }
      : {})
  };

  const downloadButton = (
    <DownloadButton
      onClick={download}
      disabled={downloading || fileUnavailable || filesPending}
    />
  );

  return (
    <>
      <EditorHeader
        dealId={dealId}
        dealName={dealName}
        title={
          <ProposalTitle
            name={name}
            editing={renaming}
            onRename={rename}
            onDone={() => setRenaming(false)}
          />
        }
        actions={
          <>
            {fileUnavailable ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* A disabled button fires no hover, so the span carries it. */}
                  <span tabIndex={0} data-test="proposal-download-pending">
                    {downloadButton}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {t("contracts.proposals.editor.signedFilePending")}
                </TooltipContent>
              </Tooltip>
            ) : (
              downloadButton
            )}
            {isRevisable(proposal) ? (
              <Button
                variant="secondary"
                onClick={() => setConfirming("revise")}
                disabled={busy}
                data-test="proposal-revise"
              >
                <IconContainer
                  as="span"
                  size="sm"
                  color="secondary"
                  className="shrink-0"
                >
                  <Edit />
                </IconContainer>
                {t("contracts.proposals.editor.revise")}
              </Button>
            ) : null}
            <ProposalActionsMenu options={menuOptions} />
          </>
        }
      />
      <div className={PANE_ROW}>
        <div className="flex flex-col gap-2 w-[400px] shrink-0 overflow-y-auto">
          <RecipientStatusList recipients={recipients} />
        </div>
        <DocumentPane
          proposal={proposal}
          url={shownUrl}
          failed={fileFailed}
          refreshing={revising || withdrawing}
          trackingPaused={trackingPaused}
        />
      </div>
      <ReviseProposalDialog
        proposal={confirming === "revise" ? proposal : undefined}
        recipients={document.recipients.list}
        withdraws={withdrawsOnRevise(proposal)}
        onConfirm={() => returnToDraft(false)}
        onCancel={() => setConfirming(undefined)}
      />
      <WithdrawProposalDialog
        proposal={confirming === "withdraw" ? proposal : undefined}
        recipients={document.recipients.list}
        onConfirm={withdraw}
        onCancel={() => setConfirming(undefined)}
      />
      {/* Only one still out for signature is rebuilt from here. */}
      <WithdrawAndRegenerateDialog
        proposal={confirming === "regenerate" ? proposal : undefined}
        recipients={document.recipients.list}
        onConfirm={() => returnToDraft(true)}
        onCancel={() => setConfirming(undefined)}
      />
    </>
  );
};
